import {
    getFirestore,
    collection as fsCollection,
    doc as fsDoc,
    getDocs,
    getDoc,
    setDoc,
    updateDoc,
    deleteDoc,
    query,
    where as fsWhere,
    orderBy as fsOrderBy,
    limit as fsLimit,
    serverTimestamp,
    onSnapshot,
    addDoc,
    Firestore,
    DocumentData,
    DocumentSnapshot,
    QuerySnapshot,
    QueryConstraint,
    WhereFilterOp,
    OrderByDirection,
    CollectionReference,
    Query,
    Unsubscribe,
    connectFirestoreEmulator,
} from 'firebase/firestore'
import {
    firebaseApp,
    useFirebaseEmulator,
    emulatorHost,
} from './init-firebase'
import { setAppBusyAction } from '../../reducers/actions/app'
import { processQuerySnapshot } from './process-query-snapshot'

let firestoreInstance: Firestore | null = null

/**
 * Lazy Firestore singleton
 */
const getDb = (): Firestore => {
    if (!firestoreInstance) {
        firestoreInstance = getFirestore(firebaseApp)
        if (useFirebaseEmulator()) {
            connectFirestoreEmulator(firestoreInstance, emulatorHost(), 8080)
        }
    }
    return firestoreInstance
}

/**
 * Snapshot-like wrapper that mimics the v8 DocumentSnapshot API while delegating
 * to a modular firebase DocumentSnapshot.
 */
interface CompatDocSnapshot {
    id: string
    exists: boolean
    data(): DocumentData | undefined
}

const wrapDocSnapshot = (snap: DocumentSnapshot): CompatDocSnapshot => ({
    id: snap.id,
    exists: snap.exists(),
    data: () => snap.data(),
})

/**
 * v8-compat document handle. Supports get/set/update/delete/onSnapshot.
 */
class DocCompat {
    private readonly collectionName: string
    private readonly docId: string

    constructor(collectionName: string, docId: string) {
        this.collectionName = collectionName
        this.docId = docId
    }

    private ref() {
        return fsDoc(getDb(), this.collectionName, this.docId)
    }

    set(data: DocumentData): Promise<void> {
        return setDoc(this.ref(), data)
    }

    update(data: DocumentData): Promise<void> {
        return updateDoc(this.ref(), data)
    }

    delete(): Promise<void> {
        return deleteDoc(this.ref())
    }

    get(): Promise<CompatDocSnapshot> {
        return getDoc(this.ref()).then(wrapDocSnapshot)
    }

    onSnapshot(callback: (snap: CompatDocSnapshot) => void): Unsubscribe {
        return onSnapshot(this.ref(), snap => callback(wrapDocSnapshot(snap)))
    }
}

/**
 * v8-compat query handle. Supports chained where/orderBy/limit before get/onSnapshot.
 */
class QueryCompat {
    private readonly collectionName: string
    private readonly constraints: QueryConstraint[]

    constructor(collectionName: string, constraints: QueryConstraint[] = []) {
        this.collectionName = collectionName
        this.constraints = constraints
    }

    private ref(): Query {
        const base: CollectionReference = fsCollection(getDb(), this.collectionName)
        return this.constraints.length > 0 ? query(base, ...this.constraints) : base
    }

    where(field: string, op: WhereFilterOp, value: unknown): QueryCompat {
        return new QueryCompat(this.collectionName, [
            ...this.constraints,
            fsWhere(field, op, value),
        ])
    }

    orderBy(field: string, direction: OrderByDirection = 'asc'): QueryCompat {
        return new QueryCompat(this.collectionName, [
            ...this.constraints,
            fsOrderBy(field, direction),
        ])
    }

    limit(count: number): QueryCompat {
        return new QueryCompat(this.collectionName, [
            ...this.constraints,
            fsLimit(count),
        ])
    }

    get(): Promise<QuerySnapshot> {
        return getDocs(this.ref())
    }

    onSnapshot(callback: (snap: QuerySnapshot) => void): Unsubscribe {
        return onSnapshot(this.ref(), callback)
    }
}

/**
 * v8-compat collection handle that exposes doc / where / orderBy / limit / get / onSnapshot / add.
 */
class CollectionCompat extends QueryCompat {
    private readonly name: string

    constructor(collectionName: string) {
        super(collectionName)
        this.name = collectionName
    }

    doc(id: string): DocCompat {
        return new DocCompat(this.name, id)
    }

    add(data: DocumentData) {
        return addDoc(fsCollection(getDb(), this.name), data)
    }
}

/**
 * v8-compatible firestore collection helper.
 *
 * @param {string} collectionName
 * @return {CollectionCompat}
 */
export const database = (collectionName: string): CollectionCompat =>
    new CollectionCompat(collectionName)

/**
 * UTC server timestamp sentinel value.
 */
export const utcTimestamp = () => serverTimestamp()

/**
 * Read data from Firestore once
 *
 * @param {string} databaseName
 * @return {Promise<DocumentData[] | void>}
 */
export const readData = (databaseName: string): Promise<DocumentData[] | void> => (
    database(databaseName)
        .get()
        .then(querySnapshot => {
            const result: DocumentData[] = []
            querySnapshot.forEach(document => {
                result.push(document.data())
            })
            return result
        })
        .catch(error => {
            console.error('Error getting document:', error)
        })
        .finally(() => setAppBusyAction(false))
)

/**
 * Listen for Firestore data changes and call callback on change
 *
 * @param {string} databaseName
 * @param {Function} callback
 * @return {Promise<void>}
 */
export const listenData = (
    databaseName: string,
    callback: (result: DocumentData[]) => void,
): Promise<void> => (
    database(databaseName)
        .get()
        .then(processQuerySnapshot)
        .then(result => callback(result))
)
